import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check, Download, Plug, Upload } from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/Spinner';
import { useDebouncedValue } from '../metaAds/useDebouncedValue';
import { recomputeRowFlags } from '../../lib/onboardingMapping';
import {
  applyCsvToMapping,
  mappingRowsToCsv,
  parseMappingCsv,
} from '../../lib/onboardingMappingCsv';
import { isRunnableRow, type BulkMappingRow, type BulkRunStatus } from '../../types/bulkOnboarding';
import { driveBulkRun, stepInFlightNotice } from './bulkRunDrive';
import {
  clearBulkRun,
  clearBulkSession,
  continueBulkRun,
  getBulkConnectUrl,
  getBulkMapping,
  getBulkSession,
  runBulkOnboard,
  searchBulkAdAccounts,
  searchBulkPages,
  type BulkRunStep,
} from './api';

// Bulk onboarding — single-token batch connect. Authorize once, build + review
// the page/ad-account mapping (with CSV round-trip for ~500 rows), then run
// the batch. See the module header for the contract.
//
// ⚠️ The Meta authorization is an AGENCY-WIDE SHARED session (2026-08-06): one
// person authorizes, the whole team can use it. Two consequences show up here —
// the authorize strip names WHO authorized (and says it's shared), and the run
// banners name WHO started the live/interrupted run, because that run is now
// shared state someone else may have kicked off.

// The starter of a run, for display. Legacy run records predate the field, so a
// null reads as "someone" rather than inventing a name.
function startedBy(run: BulkRunStatus | null): string {
  return run?.startedByName || 'another user';
}

export function BulkOnboardingPage() {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<BulkMappingRow[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Poll the session — its `run` carries live per-client progress. Auto-refetch
  // every 3s while a run is in progress so the button settles + statuses update.
  const session = useQuery({
    queryKey: ['bulkSession'],
    queryFn: getBulkSession,
    refetchInterval: (q) => (q.state.data?.run.active ? 3000 : false),
  });
  const active = session.data?.session.active ?? false;
  const runStatus = session.data?.run ?? null;
  // A started run that hasn't finished — either actively progressing or
  // interrupted (the request-held chunk loop died). Either way the user must
  // resume or clear it before starting a new batch. (An idle/no-run status has
  // active=interrupted=finished=false, so this is correctly false then.)
  const hasUnfinishedRun = !!runStatus && (runStatus.active || runStatus.interrupted);

  const authorize = useMutation({
    mutationFn: getBulkConnectUrl,
    onSuccess: ({ authUrl }) => {
      window.location.href = authUrl;
    },
  });
  const deauth = useMutation({
    mutationFn: clearBulkSession,
    onSuccess: () => {
      setRows(null);
      void queryClient.invalidateQueries({ queryKey: ['bulkSession'] });
    },
  });

  const build = useMutation({
    mutationFn: (refresh: boolean) => getBulkMapping(refresh),
    onSuccess: (res) => {
      setRows(res.rows);
      setNotice(
        res.adAccountsAvailable
          ? `${res.rows.length} club(s) to map · ${res.masterPageCount} pages, ${res.masterAdAccountCount} ad accounts available`
          : `${res.rows.length} club(s) to map · ${res.masterPageCount} pages. Ad-account list unavailable — pick ad accounts via CSV or paste act_ ids.`,
      );
    },
  });

  // Push a fresh run status into the session cache so the table + banner update
  // immediately after each held chunk (the 3s poll is a fallback).
  function applyRunStep(step: BulkRunStep) {
    queryClient.setQueryData(
      ['bulkSession'],
      (old: typeof session.data | undefined) => (old ? { ...old, run: step.run } : old),
    );
  }

  // Drive the request-held chunk loop to completion: each call is a held
  // request that advances the run, pinning the instance while work happens.
  // `start` kicks the first chunk (POST /bulk/run) or resumes (continue).
  // ⚠️ A lost response (a 524, a dropped connection) is RE-READ, not reported as
  // a failure — the run's truth is in Firestore. `priorRunStartedAt` is the run
  // that existed before a NEW start, so an old finished run is never mistaken for
  // ours; null when resuming. See bulkRunDrive.ts.
  async function driveRun(start: () => Promise<BulkRunStep>, priorRunStartedAt: string | null) {
    return driveBulkRun(start, priorRunStartedAt, {
      continueRun: continueBulkRun,
      readRun: async () => (await getBulkSession()).run,
      sleep: (ms) => new Promise((r) => setTimeout(r, ms)),
      now: Date.now,
      onStep: applyRunStep,
    });
  }

  function settleRunQueries() {
    void queryClient.invalidateQueries({ queryKey: ['bulkSession'] });
    void queryClient.invalidateQueries({ queryKey: ['onboardings'] });
  }

  const run = useMutation({
    mutationFn: () => {
      // Only CONFIRMED + complete rows run.
      const runnable = (rows ?? []).filter(isRunnableRow);
      const priorRunStartedAt = session.data?.run.startedAt ?? null;
      return driveRun(() =>
        runBulkOnboard(
          runnable.map((r) => ({
            accountId: r.accountId,
            accountName: r.accountName,
            profileName: r.profileName,
            pageId: r.pageId!,
            pageName: r.pageName,
            adAccountId: r.adAccountId!,
          })),
        ),
        priorRunStartedAt,
      );
    },
    onSettled: settleRunQueries,
  });

  // Resume an interrupted run (or one left in-progress by a closed tab): drive
  // the chunk loop from the server-stored remaining clients.
  const resume = useMutation({
    mutationFn: () => driveRun(() => continueBulkRun(), null),
    onSettled: settleRunQueries,
  });

  const clearRun = useMutation({
    mutationFn: clearBulkRun,
    onSuccess: (res) => {
      queryClient.setQueryData(
        ['bulkSession'],
        (old: typeof session.data | undefined) => (old ? { ...old, run: res.run } : old),
      );
      void queryClient.invalidateQueries({ queryKey: ['bulkSession'] });
    },
  });

  // This tab is actively driving the chunk loop.
  const driving = run.isPending || resume.isPending;

  // The run is SHARED state, so two people can race the poll and both click
  // Onboard while it still reads idle. The loser gets a 409 naming the starter —
  // surface it, or the click looks like it silently did nothing.
  const runError = (run.error ?? resume.error) as Error | null;

  // Confirmed + complete → eligible to run.
  const readyCount = useMemo(() => (rows ?? []).filter(isRunnableRow).length, [rows]);
  // Complete but not yet confirmed → awaiting the AM's glance-and-confirm.
  const needsConfirmCount = useMemo(
    () => (rows ?? []).filter((r) => r.ready && !r.confirmed).length,
    [rows],
  );
  // Button is "busy" while this tab drives the chunk loop.
  const runBusy = driving;

  function setConfirmed(accountId: string, confirmed: boolean) {
    setRows((cur) => (cur ?? []).map((r) => (r.accountId === accountId ? { ...r, confirmed } : r)));
  }

  function confirmAllMatched() {
    setRows((cur) => (cur ?? []).map((r) => (r.ready ? { ...r, confirmed: true } : r)));
  }

  function patchRow(accountId: string, patch: Partial<BulkMappingRow>) {
    // A page or ad-account change invalidates a prior confirmation; a profile-
    // name edit leaves it intact.
    const dropsConfirmation = 'pageId' in patch || 'adAccountId' in patch;
    setRows((cur) =>
      (cur ?? []).map((r) =>
        r.accountId === accountId
          ? recomputeRowFlags({ ...r, ...patch, ...(dropsConfirmation ? { confirmed: false } : {}) })
          : r,
      ),
    );
  }

  function exportCsv() {
    if (!rows) return;
    const blob = new Blob([mappingRowsToCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk-onboarding-mapping.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  async function importCsv(file: File) {
    const text = await file.text();
    const parsed = parseMappingCsv(text);
    const known = new Map((rows ?? []).map((r) => [r.accountId, r.accountName]));
    const { rows: applied, errors } = applyCsvToMapping(parsed.rows, known);
    // Merge CSV edits onto the current rows (CSV is the source of truth for the
    // accounts it names; untouched rows keep their suggestions).
    const byId = new Map(applied.map((r) => [r.accountId, r]));
    setRows((cur) => (cur ?? []).map((r) => byId.get(r.accountId) ?? r));
    const allErrors = [...parsed.errors, ...errors];
    setNotice(
      allErrors.length
        ? `Imported ${applied.length} row(s). ${allErrors.length} issue(s): ${allErrors.slice(0, 3).join(' ')}`
        : `Imported ${applied.length} row(s) from CSV.`,
    );
  }

  return (
    <div>
      <PageHeader
        title="Bulk onboarding"
        description="Authorize Meta once, map clubs to pages + ad accounts, then connect them in a batch."
        backHref="/onboarding"
      />

      {/* Authorize */}
      <div className="mb-6 flex items-center gap-3 rounded-md border border-slate-200 p-4">
        {session.isLoading ? (
          <Spinner className="h-4 w-4" />
        ) : active ? (
          <>
            <Badge variant="success">Authorized</Badge>
            <span className="text-sm text-slate-600">
              Authorized by{' '}
              {session.data!.session.authorizedByName ??
                session.data!.session.userName ??
                'a team member'}
              {session.data!.session.expiresAt
                ? ` · expires ${new Date(session.data!.session.expiresAt).toLocaleDateString()}`
                : ''}{' '}
              · shared with the team
            </span>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" onClick={() => deauth.mutate()} disabled={deauth.isPending}>
                Clear
              </Button>
              <Button
                size="sm"
                onClick={() => build.mutate(rows !== null)}
                disabled={build.isPending}
              >
                {build.isPending ? 'Building…' : rows ? 'Rebuild mapping' : 'Build mapping'}
              </Button>
            </div>
          </>
        ) : (
          <>
            <span className="text-sm text-slate-600">
              Authorize Meta once to establish the reusable bulk session.
            </span>
            <Button className="ml-auto" size="sm" onClick={() => authorize.mutate()} disabled={authorize.isPending}>
              <Plug className="h-4 w-4" />
              {authorize.isPending ? 'Opening…' : 'Authorize Meta for bulk onboarding'}
            </Button>
          </>
        )}
      </div>

      {notice && <p className="mb-4 text-sm text-slate-500">{notice}</p>}

      {runError && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
          <span>{runError.message}</span>
        </div>
      )}

      {runStatus?.paymentRequired && (
        <div className="mb-6 flex items-start gap-3 rounded-md border border-rose-200 bg-rose-50 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
          <div className="text-sm text-rose-800">
            <p className="font-semibold">Zernio account limit reached</p>
            <p className="mt-0.5">{runStatus.paymentRequired.message}</p>
            <p className="mt-1 text-rose-700">
              Remaining clients were blocked — contact Miki / Zernio billing, then re-run.
            </p>
          </div>
        </div>
      )}

      {/* Interrupted run — the request-held chunk loop died (instance reclaimed
          / tab closed). Self-heal: surface it as resumable instead of a dead
          permanent "in progress". */}
      {runStatus?.interrupted && !driving && (
        <div className="mb-3 flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
          <span>
            Batch started by {startedBy(runStatus)} was interrupted — {runStatus.results.length}/
            {runStatus.total} settled. Anyone can resume it to finish the rest, or clear it.
          </span>
          <div className="ml-auto flex gap-2">
            <Button size="sm" onClick={() => resume.mutate()} disabled={resume.isPending}>
              {resume.isPending ? 'Resuming…' : 'Resume'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => clearRun.mutate()}
              disabled={clearRun.isPending}
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      {runStatus && (runStatus.active || driving || runStatus.finished) && !runStatus.interrupted && (
        <p className="mb-3 text-sm text-slate-600">
          {runStatus.finished
            ? `Last run (started by ${startedBy(runStatus)}) — `
            : `Batch running — started by ${startedBy(runStatus)}, `}
          {runStatus.results.length}/{runStatus.total} settled
          {runStatus.finished ? ' (done)' : '…'}
          {/* The session is shared, so an active run may be someone else's —
              say why the Onboard button is disabled rather than leaving it
              inertly greyed out. */}
          {runStatus.active && !driving && (
            <span className="ml-1 text-slate-500">
              Wait for it to finish before starting another batch.
            </span>
          )}
        </p>
      )}

      {/* A step holds the run's lease. See stepInFlightNotice for why this line
          exists: a silent multi-minute "in progress" is what makes people click. */}
      {stepInFlightNotice(runStatus ?? null, driving) && (
        <p className="mb-3 text-sm text-slate-500" data-testid="step-in-flight">
          {stepInFlightNotice(runStatus ?? null, driving)}
        </p>
      )}

      {rows && (
        <>
          <div className="mb-3 flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportCsv}>
              <Download className="h-4 w-4" /> Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
              <Upload className="h-4 w-4" /> Import CSV
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void importCsv(f);
                e.target.value = '';
              }}
            />
            <div className="ml-auto flex items-center gap-3">
              {needsConfirmCount > 0 && (
                <Button variant="outline" size="sm" onClick={confirmAllMatched} disabled={runBusy}>
                  <Check className="h-4 w-4" /> Confirm all matched ({needsConfirmCount})
                </Button>
              )}
              <span className="text-sm text-slate-600">{readyCount} ready</span>
              <Button
                size="sm"
                onClick={() => run.mutate()}
                // Disabled while a run is unfinished — resume or clear it first.
                disabled={runBusy || hasUnfinishedRun || readyCount === 0}
              >
                {runBusy ? 'Onboarding…' : `Onboard ${readyCount} ready`}
              </Button>
            </div>
          </div>

          <MappingTable rows={rows} run={runStatus} onPatch={patchRow} onConfirm={setConfirmed} />
        </>
      )}

      {!active && !session.isLoading && (
        <p className="text-sm text-slate-500">
          Prefer one club at a time? Use the{' '}
          <Link to="/onboarding" className="text-kyck-700 underline">
            single-club wizard
          </Link>
          .
        </p>
      )}
    </div>
  );
}

const STATUS_BADGE: Record<string, { variant: 'success' | 'danger' | 'neutral' | 'info'; label: string }> = {
  complete: { variant: 'success', label: 'Complete' },
  failed: { variant: 'danger', label: 'Failed' },
  blocked: { variant: 'neutral', label: 'Blocked' },
  skipped: { variant: 'neutral', label: 'Skipped' },
};

function MappingTable({
  rows,
  run,
  onPatch,
  onConfirm,
}: {
  rows: BulkMappingRow[];
  run: BulkRunStatus | null;
  onPatch: (accountId: string, patch: Partial<BulkMappingRow>) => void;
  onConfirm: (accountId: string, confirmed: boolean) => void;
}) {
  const resultByAccount = useMemo(
    () => new Map((run?.results ?? []).map((r) => [r.accountId, r])),
    [run],
  );

  return (
    <div className="overflow-x-auto rounded-md border border-slate-200">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2">Club</th>
            <th className="px-3 py-2">Profile name</th>
            <th className="px-3 py-2">Facebook Page</th>
            <th className="px-3 py-2">Ad account</th>
            <th className="px-3 py-2">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => {
            const result = resultByAccount.get(r.accountId);
            const badge = result ? STATUS_BADGE[result.status] : null;
            return (
              <tr key={r.accountId} className={r.needsReview ? 'bg-amber-50/40' : ''}>
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-900">{r.accountName}</div>
                  {r.needsReview && (
                    <span className="text-xs text-amber-600">needs review</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <Input
                    className="h-8 w-44 text-sm"
                    value={r.profileName}
                    onChange={(e) => onPatch(r.accountId, { profileName: e.target.value })}
                  />
                </td>
                <td className="px-3 py-2">
                  <SearchSelect
                    valueLabel={r.pageName}
                    // Expose the handle on the COLLAPSED field (the disambiguator
                    // lives in the handle for Location Pages), then the id.
                    valueSublabel={`${r.pageUsername ? `@${r.pageUsername} · ` : ''}${r.pageId ?? ''}` || null}
                    confidence={r.pageConfidence}
                    placeholder="Search by name / handle / id…"
                    search={async (q) =>
                      (await searchBulkPages(q)).results.map((p) => ({
                        id: p.id,
                        // Location Pages often share a bare brand name — show the
                        // location when present and ALWAYS the @handle + id (the
                        // reliable disambiguators) on a muted second line.
                        label: p.location ? `${p.name} — ${p.location}` : p.name,
                        sublabel: `${p.username ? `@${p.username} · ` : ''}${p.id}`,
                        name: p.name,
                        username: p.username,
                      }))
                    }
                    onPick={(opt) =>
                      onPatch(r.accountId, {
                        pageId: opt.id,
                        pageName: opt.name ?? opt.label,
                        pageUsername: opt.username ?? null,
                      })
                    }
                  />
                </td>
                <td className="px-3 py-2">
                  <SearchSelect
                    valueLabel={r.adAccountName ?? r.adAccountId}
                    valueSublabel={
                      r.adAccountSource === 'account'
                        ? `from account record · ${r.adAccountId}`
                        : r.adAccountName
                          ? r.adAccountId
                          : null
                    }
                    confidence={r.adAccountConfidence}
                    placeholder="Search / paste act_…"
                    allowRawId
                    search={async (q) =>
                      (await searchBulkAdAccounts(q)).results.map((a) => ({
                        id: a.id,
                        label: a.name || a.id,
                        sublabel: a.name ? a.id : null,
                        name: a.name || null,
                      }))
                    }
                    onPick={(opt) =>
                      onPatch(r.accountId, {
                        adAccountId: opt.id,
                        adAccountName: opt.name ?? null,
                        adAccountSource: 'manual',
                      })
                    }
                  />
                </td>
                <td className="px-3 py-2">
                  {badge ? (
                    <span>
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                      {result?.error && <div className="text-xs text-rose-600">{result.error}</div>}
                    </span>
                  ) : run?.active && isRunnableRow(r) ? (
                    <span className="flex items-center gap-1 text-xs text-slate-500">
                      <Spinner className="h-3 w-3" /> In progress…
                    </span>
                  ) : r.confirmed ? (
                    <span className="flex items-center gap-2">
                      <Badge variant="success">Ready</Badge>
                      <button
                        type="button"
                        className="text-xs text-slate-400 underline hover:text-slate-600"
                        onClick={() => onConfirm(r.accountId, false)}
                      >
                        Unconfirm
                      </button>
                    </span>
                  ) : r.ready ? (
                    <span className="flex items-center gap-2">
                      <Badge variant="warning">Needs confirmation</Badge>
                      <Button size="sm" className="h-7" onClick={() => onConfirm(r.accountId, true)}>
                        Confirm
                      </Button>
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400">incomplete</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

type SearchOpt = {
  id: string;
  label: string;
  sublabel?: string | null;
  name?: string | null; // raw name to store on pick (label may carry extra text)
  username?: string | null; // page handle to store on pick
};

// Lightweight searchable picker: type to search the master list (debounced),
// pick a result, or (for ad accounts) paste a raw act_ id.
function SearchSelect({
  valueLabel,
  valueSublabel,
  confidence,
  placeholder,
  search,
  onPick,
  allowRawId,
}: {
  valueLabel: string | null;
  valueSublabel?: string | null;
  confidence: number;
  placeholder: string;
  search: (q: string) => Promise<SearchOpt[]>;
  onPick: (opt: SearchOpt) => void;
  allowRawId?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query, 300);
  const results = useQuery({
    queryKey: ['bulkSearch', placeholder, debounced],
    queryFn: () => search(debounced),
    enabled: open,
  });

  const rawId = allowRawId && /^(act_)?\d{4,}$/.test(query.trim()) ? query.trim() : null;

  return (
    <div className="relative w-56">
      <button
        type="button"
        className="flex min-h-8 w-full items-center justify-between rounded-md border border-slate-200 px-2 py-1 text-left text-sm hover:bg-slate-50"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="min-w-0">
          <span className={valueLabel ? 'block truncate text-slate-900' : 'block text-slate-400'}>
            {valueLabel || '— select —'}
          </span>
          {valueLabel && valueSublabel && (
            <span className="block truncate text-[10px] text-slate-400">{valueSublabel}</span>
          )}
        </span>
        {valueLabel && confidence > 0 && confidence < 0.85 && (
          <span className="ml-1 shrink-0 text-[10px] text-amber-500">
            {Math.round(confidence * 100)}%
          </span>
        )}
      </button>
      {open && (
        <div className="absolute z-10 mt-1 w-72 rounded-md border border-slate-200 bg-white p-2 shadow-lg">
          <Input
            autoFocus
            className="h-8 text-sm"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
          />
          <ul className="mt-1 max-h-56 overflow-y-auto">
            {rawId && (
              <li>
                <button
                  type="button"
                  className="w-full rounded px-2 py-1 text-left text-sm hover:bg-kyck-50"
                  onClick={() => {
                    const id = rawId.startsWith('act_') ? rawId : `act_${rawId}`;
                    onPick({ id, label: id, name: null });
                    setOpen(false);
                  }}
                >
                  Use id “{rawId}”
                </button>
              </li>
            )}
            {results.isFetching && <li className="px-2 py-1 text-xs text-slate-400">Searching…</li>}
            {(results.data ?? []).map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  className="w-full rounded px-2 py-1 text-left text-sm hover:bg-kyck-50"
                  onClick={() => {
                    onPick(o);
                    setOpen(false);
                  }}
                >
                  <span className="block truncate">{o.label}</span>
                  {/* Always show the id (muted) — same-named Location Pages are
                      only distinguishable by it. */}
                  {o.sublabel && (
                    <span className="block truncate text-[10px] text-slate-400">{o.sublabel}</span>
                  )}
                </button>
              </li>
            ))}
            {!results.isFetching && (results.data ?? []).length === 0 && !rawId && (
              <li className="px-2 py-1 text-xs text-slate-400">No matches.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
